import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../utils/helpers';

const CONTROL =
  'w-full rounded-lg border bg-white px-3 py-2 text-sm text-secondary-900 placeholder:text-secondary-400 transition-colors ' +
  'focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-500';

const controlState = (error?: string) =>
  error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
    : 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500';

interface FieldProps {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  id: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, error, hint, required, id, children, className }: FieldProps) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-secondary-700">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="mt-1 text-xs text-secondary-500">{hint}</p>
      )}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  icon?: ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, className, wrapperClassName, id, required, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <Field label={label} error={error} hint={hint} required={required} id={inputId} className={wrapperClassName}>
        <div className="relative">
          {icon && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-secondary-400">{icon}</span>
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${inputId}-error` : undefined}
            className={cn(CONTROL, controlState(error), icon && 'pl-9', className)}
            {...props}
          />
        </div>
      </Field>
    );
  },
);
Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, wrapperClassName, id, required, rows = 3, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <Field label={label} error={error} hint={hint} required={required} id={inputId} className={wrapperClassName}>
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cn(CONTROL, controlState(error), 'resize-y', className)}
          {...props}
        />
      </Field>
    );
  },
);
Textarea.displayName = 'Textarea';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className, wrapperClassName, id, required, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <Field label={label} error={error} hint={hint} required={required} id={inputId} className={wrapperClassName}>
        <select
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cn(CONTROL, controlState(error), 'pr-8', className)}
          {...props}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
    );
  },
);
Select.displayName = 'Select';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ label, className, ...props }, ref) => (
  <label className={cn('inline-flex cursor-pointer select-none items-center gap-2 text-sm text-secondary-700', className)}>
    <input
      ref={ref}
      type="checkbox"
      className="h-4 w-4 rounded border-secondary-300 text-primary-600 accent-primary-600 focus:ring-primary-500"
      {...props}
    />
    {label}
  </label>
));
Checkbox.displayName = 'Checkbox';
